import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import { type SessionTopicStatsRun } from "../SessionTopicRecord";

import { sessionQueryKeys } from "./sessionQueryKeys";

/** How often a scheduled or running topic stats run is checked again. */
const RUNNING_POLL_INTERVAL_MS = 10_000;

function inFlight(run: SessionTopicStatsRun | undefined): boolean {
  return run?.status === "scheduled" || run?.status === "running";
}

/** Whether `run` may have published stats that were not out when `previous` was read. */
function mayHaveNewStats(
  previous: SessionTopicStatsRun | undefined,
  run: SessionTopicStatsRun,
): boolean {
  if (previous === undefined || inFlight(run)) {
    return false;
  }
  return (
    inFlight(previous) ||
    previous.invocation_id !== run.invocation_id ||
    previous.started !== run.started
  );
}

/**
 * Where the latest run Roboto triggered to compute a session's topic stats
 * stands, checked again every 10 seconds while it is scheduled or running and
 * the last check succeeded (in a background tab too), whenever the page comes
 * back to the foreground, and as soon as `announcements` (how many times the
 * session has been announced ingested) goes up, since an announcement
 * schedules a run while the org's session-metrics trigger is installed and
 * enabled. When a check finds a run stopped that was in flight, or a different
 * stopped run, the session's topics and topic stats are refetched to show
 * whatever it published.
 */
export function useSessionTopicStatsRun({
  sessionId,
  announcements,
  enabled = true,
}: {
  sessionId: string;
  announcements: number;
  enabled?: boolean;
}): UseQueryResult<SessionTopicStatsRun, Error> {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();
  const announcementsSeen = React.useRef(announcements);
  React.useEffect(() => {
    if (announcements > announcementsSeen.current) {
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.topicStatsRun(sessionId),
      });
    }
    announcementsSeen.current = announcements;
  }, [queryClient, sessionId, announcements]);
  return useQuery<SessionTopicStatsRun, Error>({
    queryKey: sessionQueryKeys.topicStatsRun(sessionId),
    queryFn: async ({ signal }) => {
      const run = await sessionService.getTopicStatsRun(sessionId, {
        abortSignal: signal,
      });
      const previous = queryClient.getQueryData<SessionTopicStatsRun>(
        sessionQueryKeys.topicStatsRun(sessionId),
      );
      if (mayHaveNewStats(previous, run)) {
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.topics(sessionId),
        });
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.allTopicStats(sessionId),
        });
      }
      return run;
    },
    enabled,
    // A run can stop at any moment, so a cached answer is never trusted: the
    // run is checked again whenever the tab mounts or the page comes back to
    // the foreground. Without this the app's five-minute stale time kept a
    // "running" answer on screen after the run had finished, until the next
    // poll, which a browser holds back for a minute or more in a tab that was
    // hidden.
    staleTime: 0,
    // A failed check keeps the last run as `data`; without the status check a
    // route that refuses or fails would be polled until the page closes.
    refetchInterval: (query) =>
      query.state.status === "success" && inFlight(query.state.data)
        ? RUNNING_POLL_INTERVAL_MS
        : false,
    // Keep checking in a background tab, so a run that finishes there has
    // already refreshed the stats when the user comes back.
    refetchIntervalInBackground: true,
  });
}
